import dummyData from "../data/dummy-data.json";

const DEMO_DATA_KEY = "pulsar_demo_data";

class DemoDataManager {
  constructor() {
    this.initializeData();
  }

  // Initialize demo data from localStorage or use defaults
  initializeData() {
    const stored = localStorage.getItem(DEMO_DATA_KEY);
    if (!stored) {
      this.resetData();
    }
  }

  // Get current demo data
  getData() {
    const stored = localStorage.getItem(DEMO_DATA_KEY);
    const data = stored ? JSON.parse(stored) : this.getDefaultData();

    // Validate and fix transactions to ensure they all have details array
    if (data.transactions && Array.isArray(data.transactions)) {
      data.transactions = data.transactions.map((tx) => {
        if (!tx.details || !Array.isArray(tx.details)) {
          // Add missing details array
          return {
            ...tx,
            details: [
              {
                address: tx.to_address || data.user?.username || "unknown",
                category: tx.type === "send" ? "send" : "receive",
                amount: tx.amount,
                label: data.user?.username || "demo_user",
              },
            ],
          };
        }
        return tx;
      });
      // Save the fixed data back
      this.saveData(data);
    }

    return data;
  }

  // Get default data from dummy-data.json
  getDefaultData() {
    return JSON.parse(JSON.stringify(dummyData));
  }

  // Save demo data to localStorage
  saveData(data) {
    localStorage.setItem(DEMO_DATA_KEY, JSON.stringify(data));
  }

  // Reset demo data to defaults
  resetData() {
    const defaultData = this.getDefaultData();
    this.saveData(defaultData);
    // Clear pending transaction data
    localStorage.removeItem("temp_txn_data");
    return defaultData;
  }

  // Update wallet balance
  updateBalance(amount, operation = "subtract") {
    const data = this.getData();
    const currentBalance = parseFloat(data.wallets.pulsar_data.balance);

    if (operation === "subtract") {
      data.wallets.pulsar_data.balance = currentBalance - parseFloat(amount);
    } else if (operation === "add") {
      data.wallets.pulsar_data.balance = currentBalance + parseFloat(amount);
    } else if (operation === "set") {
      data.wallets.pulsar_data.balance = parseFloat(amount);
    }

    this.saveData(data);
    return data.wallets.pulsar_data.balance;
  }

  // Update staking data
  updateStaking(amount, operation = "add") {
    const data = this.getData();
    const currentStaked = parseFloat(
      data.staking.user_stake_details.data.user.user_stake_balance || 0,
    );
    const currentBalance = parseFloat(data.wallets.pulsar_data.balance);

    if (operation === "add") {
      data.staking.user_stake_details.data.user.user_stake_balance =
        currentStaked + parseFloat(amount);
      // Deduct from balance
      data.wallets.pulsar_data.balance = currentBalance - parseFloat(amount);
    } else if (operation === "subtract") {
      data.staking.user_stake_details.data.user.user_stake_balance = Math.max(
        0,
        currentStaked - parseFloat(amount),
      );
      // Add back to balance
      data.wallets.pulsar_data.balance = currentBalance + parseFloat(amount);
    }

    this.saveData(data);
    return data.staking.user_stake_details.data.user.user_stake_balance;
  }

  // Add transaction to history
  addTransaction(transaction) {
    const data = this.getData();
    const user = data.user;
    const timestamp = Math.floor(Date.now() / 1000);

    // Create transaction in the format expected by TransactionHistory component
    const newTransaction = {
      txid: `txid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      time: timestamp,
      type: transaction.type,
      amount: transaction.amount,
      confirmations: transaction.confirmations || 1,
      blockheight: 125430 + Math.floor(Math.random() * 1000),
      fee: transaction.fee || 0.0001,
      details: [
        {
          address: transaction.to_address || user.username,
          category: transaction.type === "send" ? "send" : "receive",
          amount: transaction.amount,
          label: user.username,
        },
      ],
      // Additional fields for new transaction types
      ...(transaction.status && { status: transaction.status }),
    };

    // Add to beginning of transactions array
    data.transactions.unshift(newTransaction);

    // Save updated data
    this.saveData(data);

    return newTransaction;
  }

  // Get explorer data
  getExplorerData() {
    const data = this.getData();
    return data.explorer;
  }

  // Update user data
  updateUser(updates) {
    const data = this.getData();
    data.user = { ...data.user, ...updates };
    this.saveData(data);
    return data.user;
  }

  // Get user data
  getUser() {
    const data = this.getData();
    return data.user;
  }

  // Get wallet data
  getWalletData() {
    const data = this.getData();
    return data.wallets;
  }

  // Get transactions
  getTransactions() {
    const data = this.getData();
    return data.transactions;
  }

  // Get staking data
  getStakingData() {
    const data = this.getData();
    return data.staking;
  }
}

// Export singleton instance
const demoDataManager = new DemoDataManager();
export default demoDataManager;
